// Turns useContestConfirmGate's model into the two merged components, and
// renders nothing when there is no pending outcome to show. The card renders
// whether or not the dialog is open, which is R1's persistent card, and the
// reason "Not yet" leaves something on pool home rather than an empty slot.
//
// `reviveInstead` implements OQ1's resolution (ISSUE-649 plan.md `## 0.5` and
// the "OQ1 resolution" note at its foot): the gate navigates to the members
// list, where the Commissioner picks an eliminated member and opens the
// revive sheet from that row. The gate itself unmounts and reappears, freshly
// recomputed, when they return to pool home.

import { useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { openPoolMembersSearch } from "../poolMembers/poolMembersRoute";
import { ContestConfirmGateDialog } from "./ContestConfirmGateDialog";
import { PoolHomeConfirmCard } from "./PoolHomeConfirmCard";
import { useContestConfirmGate } from "./useContestConfirmGate";

export type typePoolHomeConfirmGateProps = {
  poolId: string;
  contestId?: string | undefined;
};

export const PoolHomeConfirmGate = ({
  poolId,
  contestId,
}: typePoolHomeConfirmGateProps): ReactNode => {
  const navigate = useNavigate({ from: "/pools" });
  const model = useContestConfirmGate({ poolId, selectedContestId: contestId });

  const reviveInstead = (): void => {
    void navigate({ search: (prev) => openPoolMembersSearch(prev) });
  };

  if (model.phase === "hidden") return null;

  return (
    <>
      <PoolHomeConfirmCard
        winners={model.winners}
        medalName={model.medalName}
        daysUntilAutoConfirm={model.status.daysUntilAutoConfirm}
        onReviewAndConfirm={model.reviewAndConfirm}
      />
      <ContestConfirmGateDialog
        open={model.isDialogOpen}
        view={model.view}
        status={model.status}
        namingWindowWarning={model.namingWindowWarning}
        outstandingRoundLabel={model.outstandingRoundLabel}
        onConfirm={model.confirm}
        onNotYet={model.notYet}
        onReviveInstead={reviveInstead}
        onSaveNamedWinners={model.saveNamedWinners}
        onKeepPlaying={model.keepPlaying}
        isConfirming={model.isConfirming}
        isSavingNamedWinners={model.isSavingNamedWinners}
        isKeepingPlaying={model.isKeepingPlaying}
        canReviveInstead={model.canReviveInstead}
      />
    </>
  );
};
